import React, { useState, useEffect } from 'react';
import { DataSheet, CreateDataSheetItemData } from '../../types/dataSheetTypes';
import Button from '../common/Button/Button';
import InputField from '../common/InputField/InputField';
import styles from './DataSheetForm.module.scss';

interface DataSheetFormProps {
  dataSheet?: DataSheet | null;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  loading?: boolean;
}

const DataSheetForm: React.FC<DataSheetFormProps> = ({
  dataSheet,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [sheetName, setSheetName] = useState('');
  const [items, setItems] = useState<CreateDataSheetItemData[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (dataSheet) {
      setSheetName(dataSheet.sheet_name);
      setItems(dataSheet.items?.map(item => ({
        item_des: item.item_des,
        value: item.value,
        value1: item.value1,
        valueType: item.valueType,
        valueType1: item.valueType1
      })) || []);
    }
  }, [dataSheet]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!sheetName.trim()) {
      newErrors.sheetName = 'Sheet name is required';
    }

    // Validate items
    items.forEach((item, index) => {
      if (!item.item_des.trim()) {
        newErrors[`item_${index}_des`] = 'Item description is required';
      }
      if (!item.value.trim()) {
        newErrors[`item_${index}_value1`] = 'Item value is required';
      }
      if (!item.value1.trim()) {
        newErrors[`item_${index}_value2`] = 'Item value 1 is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const formData = {
      sheet_name: sheetName.trim(),
      items: items.filter(item => item.item_des.trim() && item.value.trim() && item.value1.trim())
    };

    onSubmit(formData);
  };

  const addItem = () => {
    setItems([...items, { item_des: '', value: '', value1: '', valueType: 'text', valueType1: 'text' }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: 'item_des' | 'value' | 'value1' | 'valueType' | 'valueType1', value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formSection}>
        <h3>Data Sheet Information</h3>
        
        <InputField
          label="Sheet Name"
          name="sheetName"
          type="text"
          value={sheetName}
          onChange={(e) => setSheetName(e.target.value)}
          placeholder="Enter sheet name"
          error={errors.sheetName}
          required
        />
      </div>

      <div className={styles.formSection}>
        <div className={styles.sectionHeader}>
          <h3>Items</h3>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addItem}
          >
            Add Item
          </Button>
        </div>

        {items.length === 0 ? (
          <div className={styles.emptyItems}>
            <p>No items added yet. Click "Add Item" to get started.</p>
          </div>
        ) : (
          <div className={styles.itemsList}>
            {items.map((item, index) => (
              <div key={index} className={styles.itemRow}>
                <div className={styles.itemFields}>
                  <InputField
                    label="Description"
                    name={`item_${index}_des`}
                    type="text"
                    value={item.item_des}
                    onChange={e => updateItem(index, 'item_des', e.target.value)}
                    placeholder="Enter item description"
                    error={errors[`item_${index}_des`]}
                    required
                  />
                  <select
                    value={item.valueType}
                    onChange={e => updateItem(index, 'valueType', e.target.value)}
                    style={{ marginBottom: 0, marginTop: 26, marginRight: 0, height: '49px' }}
                  >
                    {/* <option value="select">Select Type</option> */}
                    <option value="text">Text</option>
                    <option value="date">Date</option>
                  </select>
                  {item.valueType === 'date' ? (
                    <InputField
                      label="Value"
                      name={`item_${index}_value`}
                      type="date"
                      value={item.value}
                      onChange={e => updateItem(index, 'value', e.target.value)}
                      placeholder="Select date"
                      error={errors[`item_${index}_value`]}
                      required
                    />
                  ) : (
                    <InputField
                      label="Value"
                      name={`item_${index}_value`}
                      type="text"
                      value={item.value}
                      onChange={e => updateItem(index, 'value', e.target.value)}
                      placeholder="Enter item value"
                      error={errors[`item_${index}_value`]}
                      required
                    />
                  )}
                  <select
                    value={item.valueType1}
                    onChange={e => updateItem(index, 'valueType1', e.target.value)}
                    style={{ marginBottom: 0, marginTop: 26, marginRight: 0, height: '49px' }}
                  >
                    {/* <option value="select">Select Type</option> */}
                    <option value="text">Text</option>
                    <option value="date">Date</option>
                  </select>
                  {item.valueType1 === 'date' ? (
                    <InputField
                      label="Value 1"
                      name={`item_${index}_value1`}
                      type="date"
                      value={item.value1}
                      onChange={e => updateItem(index, 'value1', e.target.value)}
                      placeholder="Select date"
                      error={errors[`item_${index}_value1`]}
                      required
                    />
                  ) : (
                    <InputField
                      label="Value 1"
                      name={`item_${index}_value1`}
                      type="text"
                      value={item.value1}
                      onChange={e => updateItem(index, 'value1', e.target.value)}
                      placeholder="Enter item value 1"
                      error={errors[`item_${index}_value1`]}
                      required
                    />
                  )}
                </div>
                
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => removeItem(index)}
                  className={styles.removeButton}
                >
                  X
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.formActions}>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
        >
          {dataSheet ? 'Update Data Sheet' : 'Create Data Sheet'}
        </Button>
      </div>
    </form>
  );
};

export default DataSheetForm; 
